import { h, render, Component, createRef } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { IntlProvider, Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { QRCodeSVG } from 'qrcode.react';
import Barcode from 'react-barcode';

import localUtil from '../../lib/util';
import { defaultFieldSearchKeys } from '../../lib/fields';
import { runOnLoadFieldChecks } from '../../lib/checkValueOnLoad';
import Input from '../../components/form/input';
import { getImageFilesFromClipboard, uploadImageFile, imageMarkdown, insertTextAtCursor } from '../../lib/pasteImageUpload';
import { compareTextTemplates } from '../../lib/textTemplateSort';
import { applyTemplateFields } from '../../lib/textTemplateFields';

import definitionNo from '../../languages/no.json';
import definitionEn from '../../languages/en.json';
import { appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';

const countryMap = {
    no: definitionNo,
    en: definitionEn,
    default: definitionEn,
};

const FOCUSABLE_ELEMENTS = ['INPUT', 'TEXTAREA', 'SELECT'];

const MARKDOWN_OPTIONS = {
    pedantic: false,
    gfm: true,
    breaks: true,
    sanitize: false,
    smartLists: true,
    smartypants: true,
    xhtml: true,
};

function isValidDate(dateString) {
    const date = new Date(dateString);
    if (isNaN(date)) return false;
    if (date.getFullYear() < 0) return false;
    return true;
}


@withText(props => ({
    searchTemplatePlaceholder: <Text id='texttemplate.search-placeholder'>Search for templates...</Text>,
}))
@observer
class FormFieldsCompact extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showSection: '',
            filterSection: '',
            // Callers can pre-fill the field search (e.g. the profile-photo hero
            // opens the edit form filtered down to the image field).
            fieldSearch: props.initialFieldSearch || '',
            showOnlyFilled: false,
            showOnlyRequired: false,
            showOnlyRecommended: props.defaultShowOnlyRecommended || false,
            updateInProgress: {},
            textTemplateSaved: {},
            isInfocus: {},
            textTemplates: [],
            sectionCollapsed: {},
            showTemplateModal: false,
            templateModalSection: '',
            templateModalField: '',
            templateSearchQuery: '',
            selectedTemplatePreview: null,
            activeHelpField: null,
            pasteImageUploading: false,
        };
        this.updateTimer = {};
        this.callbackTimer = {};
        this.sectionRefs = {};
        this.styleInjected = false;
        this.fieldSearchRef = createRef();
        // Guard so observer-driven re-renders only validate each loaded object once.
        this.checkedValuesForId = null;
    }

    handleKeyDown = (e) => {
        // Don't trigger if user is typing in an input field
        if (FOCUSABLE_ELEMENTS.includes(document.activeElement?.tagName)) {
            return;
        }
        if (e.shiftKey && e.key.toLowerCase() === 'f') {
            e.preventDefault();
            this.fieldSearchRef.current?.focus();
        }
    }

    setUpdateInProgress = (field, value) => {
        const { updateInProgress } = this.state;
        this.setState({
            updateInProgress: {
                ...updateInProgress,
                [field]: value,
            },
        });
    }

    // Paste a screenshot/image directly into a textarea field.
    // Uploads the image and inserts a markdown image link at the cursor. The inserted
    // text is dispatched as an 'input' event so the normal autosave path persists it.
    // Non-image pastes (plain text) fall through untouched.
    handlePasteCapture = async (e) => {
        const target = e.target;
        if (!target || target.tagName !== 'TEXTAREA') {
            return;
        }
        const files = getImageFilesFromClipboard(e);
        if (files.length === 0) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        this.setState({ pasteImageUploading: true });
        try {
            // Upload sequentially so multiple pasted images are inserted in order.
            for (let i = 0; i < files.length; i += 1) {
                // eslint-disable-next-line no-await-in-loop
                const uploadedFile = await uploadImageFile(files[i]);
                insertTextAtCursor(target, imageMarkdown(uploadedFile));
            }
        } catch (err) {
            console.error('Paste image upload failed:', err);
        } finally {
            this.setState({ pasteImageUploading: false });
        }
    }

    debouncedCallback = (fieldName, debounceMs, callback, value) => {
        if (this.callbackTimer[fieldName]) {
            clearTimeout(this.callbackTimer[fieldName]);
        }
        this.callbackTimer[fieldName] = setTimeout(() => {
            callback(value);
            delete this.callbackTimer[fieldName];
        }, debounceMs);
    }

    adminUpdate = (e, callback, checkValue) => {
        const { id } = e.target.closest('.inputForm').dataset;
        const { datatype } = e.target.dataset;
        const idAsInt = parseInt(id, 10);
        const { name, value: rawValue, type } = e.target;
        this.setUpdateInProgress(`${id}-${name}`, true);

        let value = rawValue;
        if (type === 'number') {
            value = value.replace(',', '.');
            value = value.replace(/[^0-9.]/g, '');
        }
        if (datatype === 'decimal') {
            value = parseFloat(value);
        }
        if (datatype === 'integer') {
            value = parseInt(value, 10);
        }

        const field = `${idAsInt}-${name}`;
        const {
            holdingStore,
            isAdmin,
            objectName,
            onInputcallback = () => {},
        } = this.props;
        if (type === 'datetime-local' && !isValidDate(value)) return;
        if (type === 'date' && !isValidDate(value)) return;

        holdingStore.adminUpdateLocal(idAsInt, { [name]: value }, objectName);
        holdingStore.updateObjectKeyValue(objectName, name, value);
        onInputcallback(id, name, value);

        clearTimeout(this.updateTimer[field]);
        const debounceTime = type === 'textarea' ? 1500 : 700;
        this.updateTimer[field] = setTimeout(() => {
            this.doAdminUpdate({ id, idAsInt, name, value, callback, checkValue });
        }, debounceTime);
    }

    updateValue = async ({ name, value, id }) => {
        const { holdingStore, objectName } = this.props;
        holdingStore.updateObjectKeyValue(objectName, name, value);
        if (id) {
            const idAsInt = parseInt(id, 10);
            await holdingStore.saveField(idAsInt, name, value);
        }
    }

    doAdminUpdate = async ({ id, idAsInt, name, value, callback = () => {}, checkValue = () => {} }) => {
        const { holdingStore, isAdmin, objectName } = this.props;
        if (holdingStore) {
            if (isAdmin) {
                await holdingStore.adminUpdate(idAsInt, { [name]: value }, objectName);
            } else {
                await holdingStore.saveField(idAsInt, name, value);
            }
            this.setUpdateInProgress(`${id}-${name}`, false);
            callback(value);
            checkValue(value);
        }
    }

    displayValue = (field, val) => val;

    displayValueArrayObject = (field, val) => {
        if (field.displayArrayObject) return field.displayArrayObject(val);
        if (field.name === 'members') return `${val.firstname} ${val.lastname}`;
        if (field.name === 'tags') return val.name;
        return val?.title;
    }

    renderField(field, line) {
        const apiServer = util.getApiServer();
        if (field.showQR) {
            const value = field.getQrValue ? field.getQrValue(line) : `${apiServer}/go/${field.getValue(line)}`;
            return <QRCodeSVG value={value} size={200} />;
        }
        if (field.showImage) {
            return field.getValue(line).map((image) => (
                <img src={image.s3MediumLink} style='max-width: 60px; border-radius: 6px;' />
            ));
        }
        return <span class='compact-field-value'>{field.getValue(line)}</span>;
    }

    toggleSection = (e) => {
        const { sectionCollapsed } = this.state;
        const { key } = e.target.closest('.compact-section-header, .compact-section-collapsed').dataset;
        sectionCollapsed[key] = !sectionCollapsed[key];
        this.setState({
            sectionCollapsed,
            showOnlyRecommended: false,
        });
    }

    onFocus = (e, field, object, holdingStore, objectName) => {
        const { name } = e.target;
        const { isInfocus } = this.state;
        isInfocus[name] = true;
        this.setState({ isInfocus });
        if (field && field.focusCallback && !field.getValue(object)) {
            field.focusCallback(object, holdingStore, objectName);
        }
    }

    onBlur = (e) => {
        const { name } = e.target;
        const { isInfocus } = this.state;
        setTimeout(() => {
            delete isInfocus[name];
            this.setState({ isInfocus });
        }, 2000);
    }

    openTemplateModal = (section, field) => {
        this.setState({
            showTemplateModal: true,
            templateModalSection: section,
            templateModalField: field,
            templateSearchQuery: '',
            selectedTemplatePreview: null,
        });
    }

    closeTemplateModal = () => {
        this.setState({
            showTemplateModal: false,
            templateModalSection: '',
            templateModalField: '',
        });
    }

    handleTemplateSearch = (e) => {
        this.setState({ templateSearchQuery: e.target.value });
    }

    selectTemplateFromModal = (template) => {
        this.setState({ selectedTemplatePreview: template });
    }

    // TICKET-01482: when the caller provides the templateFields prop (a values
    // object or a function returning one), bracket codes like [animal] and
    // [weight] in the template body are filled in at insert time. Without the
    // prop the body is inserted verbatim, exactly as before.
    resolveTemplateBody = (template) => {
        if (!template) return '';
        const { templateFields } = this.props;
        const values = typeof templateFields === 'function' ? templateFields() : templateFields;
        if (!values) return template.body;
        return applyTemplateFields(template.body, values);
    }

    getTemplateFieldTokens = () => {
        const { templateFields } = this.props;
        const values = (typeof templateFields === 'function' ? templateFields() : templateFields) || {};
        return Object.keys(values).map(key => `[${key}]`);
    }

    applySelectedTemplate = async () => {
        const { selectedTemplatePreview, templateModalField } = this.state;
        if (!selectedTemplatePreview) return;

        const { holdingStore, objectName, object, objectUpdate } = this.props;
        const currentObject = object || holdingStore[objectName];
        const currentValue = currentObject[templateModalField] || '';
        const templateBody = this.resolveTemplateBody(selectedTemplatePreview);

        if (objectUpdate) {
            objectUpdate({
                id: currentObject.id,
                name: templateModalField,
                value: `${currentValue}${currentValue ? '\n\n' : ''}${templateBody}`,
                md5: currentObject.md5,
            });
        } else {
            holdingStore.updateObjectKeyValue(objectName, templateModalField, `${currentValue}${currentValue ? '\n\n' : ''}${templateBody}`);
            if (currentObject?.id) {
                await holdingStore.save({ [templateModalField]: templateBody }, currentObject.id);
            }
        }
        this.closeTemplateModal();
    }

    saveTextTemplate = async (e) => {
        const dialogAction = beginDialogAction(this, 'saveTextTemplate', () => [
            (this.props.object || this.props.holdingStore?.[this.props.objectName])?.id,
        ]);
        if (!dialogAction) return false;
        try {
            const { textTemplateStore } = this.props.stores;
            const { holdingStore, objectName, object } = this.props;
            const { section, field } = e.target.closest('button').dataset;
            const currentObject = object || holdingStore[objectName];
            const templateBody = currentObject[field];

            if (!((await appConfirm('Save this text as a template for everyone?')) && dialogAction.isCurrent())) return;

            const response = await textTemplateStore.insert({
                section,
                field,
                body: templateBody,
            });
            if (response.status === 201) {
                const { textTemplateSaved } = this.state;
                textTemplateSaved[field] = true;
                this.setState({ textTemplateSaved });
                setTimeout(() => {
                    delete textTemplateSaved[field];
                    this.setState({ textTemplateSaved });
                }, 3000);
            }
        } finally {
            dialogAction.finish();
        }
    }

    toggleAdminFields = () => {
        const { appState } = this.props.stores;
        const { showAdminFields } = appState;
        appState.setShowAdminFields(!showAdminFields);
    }

    setFilterSection = (e) => {
        const { filterSection } = this.state;
        const { section } = e.target.closest('span').dataset;
        this.setState({
            filterSection: filterSection === section ? '' : section,
            showOnlyRecommended: false,
        });
    }

    handleFieldSearch = (e) => {
        this.setState({
            fieldSearch: e.target.value,
            showOnlyRecommended: false,
        });
    }

    handleFieldSearchKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            e.preventDefault();
            this.setState({ fieldSearch: '' });
            e.target.blur();
        }
    }

    clearFieldSearch = () => {
        this.setState({ fieldSearch: '' });
    }

    toggleShowOnlyFilled = () => {
        this.setState({ showOnlyFilled: !this.state.showOnlyFilled });
    }

    toggleShowOnlyRequired = () => {
        this.setState({ showOnlyRequired: !this.state.showOnlyRequired });
    }

    toggleShowOnlyRecommended = () => {
        this.setState({ showOnlyRecommended: !this.state.showOnlyRecommended });
    }

    clearAllFilters = () => {
        this.setState({
            showOnlyFilled: false,
            showOnlyRequired: false,
            showOnlyRecommended: false
        });
    }

    loadAll = async () => {
        const { sections = [], holdingStore, sectionsNotCollapsed, videoMode, object: inputObject, objectName } = this.props;
        if (!sections || !Array.isArray(sections) || sections.length === 0) {
            return;
        }

        const object = inputObject || holdingStore[objectName];

        holdingStore.updateKeyValue('feedback', {});
        holdingStore.updateKeyValue('feedbackButtonFunc', {});
        holdingStore.updateKeyValue('feedbackButtonTitle', {});
        holdingStore.updateKeyValue('feedbackButtonProgress', {});
        holdingStore.updateKeyValue('feedbackButtonDone', {});
        holdingStore.updateKeyValue('feedbackButtonInfo', {});

        if (!sectionsNotCollapsed) {
            const sectionCollapsed = {};
            for (let i = 0; i < sections.length; i++) {
                const section = sections[i];
                if (videoMode) {
                    sectionCollapsed[section.key] = !section.isCollapsed;
                } else if (section.isCollapsed) {
                    sectionCollapsed[section.key] = true;
                }
                for (let j = 0; j < section.fields.length; j++) {
                    const field = section.fields[j];
                    if (field.before) {
                        field.before(object, holdingStore);
                    }
                }
            }
            this.setState({ sectionCollapsed });
        }

        const { textTemplateStore } = this.props.stores;
        const sectionKeys = sections.map(s => s.key);
        const tpls = await textTemplateStore.load({ query: { section: sectionKeys }, skipUpdate: 1 });
        this.setState({ textTemplates: tpls });
    }

    // Validate already-stored values when the object is loaded (e.g. a visitor
    // email saved with a typo), so the warning shows without the user retyping.
    // Only fields flagged checkValueOnLoad are checked — see lib/checkValueOnLoad.js.
    maybeCheckStoredValues = () => {
        const { sections, holdingStore, objectName, object: inputObject, isNew } = this.props;
        if (isNew || !holdingStore) return;
        const object = inputObject || holdingStore[objectName];
        if (!object?.id || this.checkedValuesForId === object.id) return;
        this.checkedValuesForId = object.id;
        runOnLoadFieldChecks({ sections, object, holdingStore });
    }

    componentDidMount() {
        this.loadAll();
        document.addEventListener('keydown', this.handleKeyDown);
        this.maybeCheckStoredValues();
    }

    componentWillReceiveProps(nextProps) {
        // A NEW pre-fill value re-applies the field search (e.g. clicking the
        // profile-photo hero while the form is already mounted). An unchanged
        // prop never overwrites what the user has typed since.
        if (nextProps.initialFieldSearch
            && nextProps.initialFieldSearch !== this.props.initialFieldSearch) {
            this.setState({ fieldSearch: nextProps.initialFieldSearch });
        }
    }

    componentDidUpdate() {
        this.maybeCheckStoredValues();
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown);
    }

    toggleHelpTooltip = (e, fieldName) => {
        e.preventDefault();
        e.stopPropagation();
        const { activeHelpField } = this.state;
        if (activeHelpField === fieldName) {
            this.setState({ activeHelpField: null });
        } else {
            this.setState({ activeHelpField: fieldName });
        }
    }

    closeHelpTooltip = () => {
        this.setState({ activeHelpField: null });
    }

    renderHelpIcon = (help, fieldName) => {
        if (!help) return null;
        const { activeHelpField } = this.state;
        const isActive = activeHelpField === fieldName;
        return (
            <span
                class={`compact-help-icon ${isActive ? 'active' : ''}`}
                onClick={(e) => this.toggleHelpTooltip(e, fieldName)}
                onMouseEnter={() => this.setState({ activeHelpField: fieldName })}
                onMouseLeave={() => this.setState({ activeHelpField: null })}
            >
                <i class='fa-solid fa-circle-info' style='font-size: 10px;' />
                <span class={`compact-help-tooltip ${isActive ? 'show' : ''}`}>
                    {help}
                    <button
                        class='compact-help-close'
                        onClick={(e) => { e.stopPropagation(); this.closeHelpTooltip(); }}
                    >
                        <i class='fa-solid fa-times' />
                    </button>
                </span>
            </span>
        );
    }

    render() {
        // Enable MobX trace to see what observables are being accessed
        if (typeof window !== 'undefined' && window.MOBX_DEBUG) {
            trace(true);
        }

        const { userStore, appState } = this.props.stores;
        const { fieldToolbar = {}, fieldAside = {} } = this.props;
        const { showAdminFields } = appState;
        const {
            updateInProgress = {},
            isInfocus,
            textTemplateSaved,
            textTemplates = [],
            sectionCollapsed = {},
            filterSection,
            fieldSearch,
            showOnlyFilled,
            showOnlyRequired,
            showOnlyRecommended,
            showTemplateModal,
            templateModalSection,
            templateModalField,
            templateSearchQuery,
            selectedTemplatePreview,
            pasteImageUploading,
        } = this.state;
        const {
            sections,
            fields,
            holdingStore,
            objectName,
            object: inputObject,
            isNew,
            drawerLevel,
            lookupArray,
            hideAdmin = !showAdminFields,
            filter = fields ? f => fields.includes(f.name) : () => true,
            onInput,
            hideFields,
            hideSections,
            toggleFieldFunction,
            toggleFieldValues = {},
            onInputcallback = () => {},
            drawerCallback = () => {},
            onKeyDown = () => {},
            openFieldSettings,
            readOnly,
            searchTemplatePlaceholder,
            isAdmin: isAdminProp,
            skipSurroundingDivs = false,
            labelWidth,
            hideFilterToolbar = false,
        } = this.props;

        const { user, isAdmin, isCustomerAdmin } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        const object = inputObject || holdingStore[objectName];

        if (!sections || !Array.isArray(sections) || !object) {
            return <div class='compact-form-container'>Loading...</div>;
        }

        const finalSections = fields ? sections.filter(s => s.fields.some(f => fields.includes(f.name))) : sections;

        // Check if any section has admin fields or is an admin section
        const hasAdminFields = finalSections?.some(s => s.isAdminSection || s.fields?.some(f => f.isAdminField));

        // Filter sections for display
        const visibleSections = finalSections?.filter(s => {
            if (hideSections?.length > 0 && hideSections.some(e => e.key === s.key)) return false;
            // Hide admin-only sections from non-admins or when admin fields are hidden
            // But keep admin sections visible when recommended filter is active and section has recommended fields
            if (s.isAdminSection && (!isAdmin || hideAdmin)) {
                if (showOnlyRecommended && isAdmin && s.fields?.some(f => f.isRecommended)) {
                    return true;
                }
                return false;
            }
            return true;
        }) || [];

        // Single source of truth for whether a field survives the active
        // display filters (field search, Utfylt/Påkrevd/Anbefalt, admin
        // visibility, hideOnCreate). Used by the field render loop AND by the
        // section-skip check, so a section whose every field is filtered away
        // hides its header instead of rendering an empty section.
        const fieldPassesFilters = (field) => {
            if (isAdmin && field.hideIfAdmin) return false;

            // Field search filter - if searching, ONLY apply search filter (bypass other filters)
            if (fieldSearch && fieldSearch.length > 0) {
                const searchLower = fieldSearch.toLowerCase();
                const fieldTitle = (field.title?.props?.children || field.title || '').toString().toLowerCase();
                const fieldPlaceholder = (field.placeholder || '').toString().toLowerCase();
                const fieldValue = (field.getValue(object) || '').toString().toLowerCase();
                const fieldName = (field.name || '').toLowerCase();
                // Use field's searchKeys if defined, otherwise fall back to default mapping
                const searchKeys = (field.searchKeys || defaultFieldSearchKeys[field.name] || []).map(k => k.toLowerCase());
                const matchesSearchKeys = searchKeys.some(key => key.includes(searchLower));

                // Get translation from language files
                const getNestedTranslation = (obj, path) => {
                    if (!path) return '';
                    const parts = path.split('.');
                    let result = obj;
                    for (const part of parts) {
                        result = result?.[part];
                        if (result === undefined) return '';
                    }
                    return result;
                };
                const translationId = field.title?.props?.id || field.placeholder?.props?.id || '';
                const translatedLabelNo = getNestedTranslation(definitionNo, translationId).toString().toLowerCase();
                const translatedLabelEn = getNestedTranslation(definitionEn, translationId).toString().toLowerCase();

                const matches = fieldTitle.includes(searchLower) ||
                    fieldPlaceholder.includes(searchLower) ||
                    fieldValue.includes(searchLower) ||
                    fieldName.includes(searchLower) ||
                    matchesSearchKeys ||
                    translatedLabelNo.includes(searchLower) ||
                    translatedLabelEn.includes(searchLower);
                if (!matches) return false;
                // When searching, skip other filters - show all matching fields
            } else {
                // Only apply these filters when NOT searching
                // Show only filled fields filter
                if (showOnlyFilled) {
                    const value = field.getValue(object);
                    const isEmpty = value === null ||
                        value === undefined ||
                        value === '' ||
                        value === 0 ||
                        value === false ||
                        (typeof value === 'string' && value.trim() === '') ||
                        (Array.isArray(value) && value.length === 0) ||
                        (typeof value === 'object' && value !== null && !Array.isArray(value) && Object.keys(value).length === 0);
                    if (isEmpty) return false;
                }

                // Show only required fields filter
                if (showOnlyRequired && !field.isRequired) {
                    return false;
                }

                // Show only recommended fields filter
                if (showOnlyRecommended && !field.isRecommended && !field.isRequired) {
                    return false;
                }
            }

            if ((field.isAdminField && hideAdmin) || (field.isAdminField && !isAdmin)) {
                // Allow recommended admin fields through when recommended filter is active
                if (!(showOnlyRecommended && isAdmin && field.isRecommended)) {
                    return false;
                }
            }
            if (isNew && field.hideOnCreate) return false;
            return true;
        };

        return (
            <div class='compact-form-container' onPasteCapture={this.handlePasteCapture}>
                {pasteImageUploading && (
                    <div
                        class='position-fixed start-50 translate-middle-x bg-dark text-white px-3 py-2 rounded shadow d-flex align-items-center'
                        style='bottom: 20px; z-index: 1080;'
                    >
                        <i class='fa-duotone fa-spinner fa-spin me-2' />
                        <Text id='imageupload.uploading-image'>Uploading image...</Text>
                    </div>
                )}
                {/* Section Filter Toolbar */}
                {visibleSections.length > 0 && !hideFilterToolbar && (
                    <div class='compact-filter-toolbar' style='padding: 8px 10px;'>
                        <div class='d-flex flex-wrap align-items-center gap-1'>
                            {/* Search input */}
                            <div class='position-relative' style='flex: 0 0 auto;'>
                                <input
                                    ref={this.fieldSearchRef}
                                    type='text'
                                    class={`form-control form-control-sm rounded ${darkmode ? 'bg-dark text-light border-secondary' : ''}`}
                                    placeholder='Søk felt... (⇧F)'
                                    value={fieldSearch}
                                    onInput={this.handleFieldSearch}
                                    onKeyDown={this.handleFieldSearchKeyDown}
                                    style='font-size: 11px; padding: 4px 28px 4px 8px; width: 120px;'
                                />
                                {fieldSearch && (
                                    <i
                                        class='fa-solid fa-times position-absolute text-muted'
                                        style='right: 8px; top: 50%; transform: translateY(-50%); cursor: pointer; font-size: 10px;'
                                        onClick={this.clearFieldSearch}
                                    />
                                )}
                            </div>
                            <span
                                class={`badge ${showOnlyFilled ? 'text-bg-primary' : (darkmode ? 'text-bg-dark border border-secondary' : 'text-bg-light')}`}
                                onClick={this.toggleShowOnlyFilled}
                                style='cursor: pointer; font-size: 11px; font-weight: 500;'
                                title='Vis kun felt med verdier'
                            >
                                <i class='fa-solid fa-filter me-1' />
                                Utfylt
                            </span>
                            <span
                                class={`badge ${showOnlyRequired ? 'text-bg-primary' : (darkmode ? 'text-bg-dark border border-secondary' : 'text-bg-light')}`}
                                onClick={this.toggleShowOnlyRequired}
                                style='cursor: pointer; font-size: 11px; font-weight: 500;'
                                title='Vis kun påkrevde felt'
                            >
                                <i class='fa-solid fa-asterisk me-1' />
                                Påkrevd
                            </span>
                            <span
                                class={`badge ${showOnlyRecommended ? 'text-bg-warning' : (darkmode ? 'text-bg-dark border border-secondary' : 'text-bg-light')}`}
                                onClick={this.toggleShowOnlyRecommended}
                                style='cursor: pointer; font-size: 11px; font-weight: 500;'
                                title='Vis kun anbefalte felt'
                            >
                                <i class='fa-solid fa-diamond me-1' />
                                Anbefalt
                            </span>
                            {isAdmin && hasAdminFields && (
                                <span
                                    class={`badge ${showAdminFields ? 'text-bg-danger' : (darkmode ? 'text-bg-dark border border-secondary' : 'text-bg-light')}`}
                                    onClick={this.toggleAdminFields}
                                    style='cursor: pointer; font-size: 11px; font-weight: 500;'
                                >
                                    <i class={`fa-solid fa-${showAdminFields ? 'eye' : 'eye-slash'} me-1`} />
                                    Admin
                                </span>
                            )}
                            {visibleSections.length > 1 && visibleSections.map((section) => {
                                const isActive = filterSection === section.key;
                                const sectionVisibleFields = section.fields.filter(filter).filter(f => {
                                    if (hideFields && hideFields.length > 0) {
                                        return !hideFields.some(e => e.name === f.name);
                                    }
                                    return true;
                                });
                                const filledCount = sectionVisibleFields.filter(f => f.getValue(object)).length;
                                const totalCount = sectionVisibleFields.length;
                                return (
                                    <span
                                        data-section={section.key}
                                        class={`badge ${isActive ? 'text-bg-primary' : (darkmode ? 'text-bg-dark border border-secondary' : 'text-bg-light')}`}
                                        onClick={this.setFilterSection}
                                        style='cursor: pointer; font-size: 11px; font-weight: 500;'
                                    >
                                        {section.name}
                                        <span class={`ms-1 ${isActive ? 'text-white' : 'text-muted'}`} style='opacity: 0.7;'>
                                            ({filledCount}/{totalCount})
                                        </span>
                                        {isActive && <i class='fa-solid fa-times ms-1' />}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Filter Warning */}
                {(showOnlyFilled || showOnlyRequired || showOnlyRecommended) && !fieldSearch && (
                    <div class='compact-filter-warning' style='padding: 6px 10px; background: #fff3cd; border-bottom: 1px solid #ffc107; font-size: 11px; color: #856404;'>
                        <i class='fa-solid fa-filter me-1' />
                        Filter aktiv: Viser kun {[
                            showOnlyFilled && 'utfylte',
                            showOnlyRequired && 'påkrevde',
                            showOnlyRecommended && 'anbefalte'
                        ].filter(Boolean).join(', ')} felt
                        <span
                            class='ms-2 text-decoration-underline'
                            style='cursor: pointer;'
                            onClick={this.clearAllFilters}
                        >
                            Fjern filter
                        </span>
                    </div>
                )}

                {visibleSections.filter(s => {
                    if (filterSection && filterSection !== '') return s.key === filterSection;
                    return true;
                }).map((section) => {
                    // Skip sections with no visible fields - every active
                    // display filter counts, so a filtered-empty section hides
                    // its header instead of rendering an empty box.
                    const sectionVisibleFields = section.fields.filter(filter).filter(f => {
                        if (hideFields && hideFields.length > 0 && hideFields.some(e => e.name === f.name)) return false;
                        if (!fieldPassesFilters(f)) return false;
                        if (f.conditionFunc && !f.conditionFunc(object)) return false;
                        return true;
                    });
                    if (sectionVisibleFields.length === 0) return null;

                    // Force expand sections when searching or filtering
                    const isCollapsed = (fieldSearch || showOnlyFilled || showOnlyRequired || showOnlyRecommended) ? false : sectionCollapsed[section.key];

                    return (
                        <div class={`compact-section ${darkmode ? 'darkmode' : ''}`} ref={c => this.sectionRefs[section.key] = c}>
                            <div class='compact-section-header' onClick={this.toggleSection} data-key={section.key}>
                                <span>
                                    {section.name}
                                    <i class={`fa-solid fa-chevron-${isCollapsed ? 'down' : 'up'} ms-2`} style='font-size: 10px;' />
                                </span>
                                <span style='font-size: 11px; opacity: 0.6;'>
                                    ({(() => {
                                        const headerVisibleFields = section.fields.filter(filter).filter(f => {
                                            if (hideFields && hideFields.length > 0) {
                                                return !hideFields.some(e => e.name === f.name);
                                            }
                                            return true;
                                        });
                                        return `${headerVisibleFields.filter(f => f.getValue(object)).length}/${headerVisibleFields.length}`;
                                    })()})
                                </span>
                            </div>

                            {isCollapsed ? (
                                <div class='compact-section-collapsed' onClick={this.toggleSection} data-key={section.key}>
                                    <i class='fa-solid fa-chevron-down me-2' />
                                    {section.displayCollapsedInfo ? section.displayCollapsedInfo(object, hideFields) : 'Tap to expand'}
                                </div>
                            ) : (
                                <div class='compact-field-table'>
                                    {section.fields.filter(filter).filter(f => {
                                        if (hideFields && hideFields.length > 0) {
                                            return !hideFields.some(e => e.name === f.name);
                                        }
                                        return true;
                                    }).map((field) => {
                                        if (!fieldPassesFilters(field)) return null;

                                        let isReadOnly = isAdmin ? (field.isReadOnly && !field.adminEditAllowed) : field.isReadOnly;
                                        if (field.noEdit && !isNew) isReadOnly = isAdmin ? !field.adminEditAllowed : true;
                                        if (readOnly) isReadOnly = true;
                                        if (isNew && field.defaultValue && !field.getValue(object)) {
                                            holdingStore.updateObjectKeyValue(objectName, field.name, field.defaultValue);
                                        }
                                        if (field.conditionFunc && !field.conditionFunc(object)) return null;

                                        const textTpls = textTemplates.filter(t => t.section === section.key && t.field === field.name);
                                        const line = field.getValue(object);
                                        const isTextarea = ['textarea', 'textareaEditor'].includes(field.type);
                                        const aside = fieldAside[field.name];
                                        const toolbar = fieldToolbar[field.name];
                                        const isToggle = ['toggle', 'checkbox'].includes(field.type);

                                        let datalist = field.datalist;
                                        if (field.datalistName && Array.isArray(holdingStore[field.datalistName])) {
                                            datalist = holdingStore[field.datalistName].map(item => [
                                                field.datalistFieldKey(item),
                                                field.datalistFieldVal(item),
                                            ]);
                                            if (field.addToDatalist) datalist.push(field.addToDatalist);
                                        }

                                        return (
                                            <div class={`compact-field-row ${field.isAdminField ? 'admin-field' : ''}`}>
                                                <div class='compact-field-label-cell' style={labelWidth ? `width: ${labelWidth}px; max-width: ${labelWidth}px; min-width: ${labelWidth}px;` : ''}>
                                                    <div class='compact-field-label-content'>
                                                        {field.title || field.placeholder}
                                                        {field.isAdminField && isAdmin && <span class='compact-admin-badge'>A</span>}
                                                        {this.renderHelpIcon(field.help, field.name)}
                                                    </div>
                                                </div>
                                                <div class={`compact-field-input-cell ${isTextarea ? 'has-textarea' : ''}`}>
                                                    {toolbar && <div class='compact-field-toolbar'>{toolbar}</div>}
                                                    {isReadOnly ? (
                                                        line ? this.renderField(field, object) : (
                                                            <span class='compact-field-value-empty'>--</span>
                                                        )
                                                    ) : (
                                                        <div class={aside ? 'compact-field-split' : undefined}>
                                                            <div class={aside ? 'compact-field-split-main' : undefined}>
                                                                <div class='compact-input-wrapper'>
                                                                    <Localizer>
                                                                        <Input
                                                                            key={`${object.id || 'isNew'}-${field.name}-${section.key}`.replace(/[^a-zA-Z0-9-]/g, '-')}
                                                                            stores={this.props.stores}
                                                                            isAdmin={isAdminProp}
                                                                            drawerLevel={drawerLevel}
                                                                            holdingStore={holdingStore}
                                                                            objectName={objectName}
                                                                            object={object}
                                                                            type={field.type}
                                                                            field={field.name}
                                                                            aiJournal={field.aiJournal}
                                                                            aiPost={field.aiPost}
                                                                            audioTranscribe={field.audioTranscribe && parseInt(userStore.customer?.featureFlags?.audioTranscribe, 10) === 1}
                                                                            section={section.key}
                                                                            fieldObject={field}
                                                                            step={field.step}
                                                                            viewHtml={field.viewHtml}
                                                                            min={field.minFunc ? field.minFunc(object) : field.min}
                                                                            max={field.maxFunc ? field.maxFunc(object) : field.max}
                                                                            value={line}
                                                                            inputValue={field.isVirtual ? line : undefined}
                                                                            displayArrayValue={(val) => this.displayValue(field, val)}
                                                                            displayArrayObject={(val) => this.displayValueArrayObject(field, val)}
                                                                            datalist={datalist}
                                                                            datalistHelp={field.datalistHelp}
                                                                            datalistValues={field.datalistValues}
                                                                            toggleFields={field.toggleFields}
                                                                            useFirstValueAsBackgroundColorForOption={field.useFirstValueAsBackgroundColorForOption}
                                                                            title={field.title}
                                                                            placeholder={field.placeholder}
                                                                            help={field.help}
                                                                            showDanger={field.showDanger}
                                                                            showWarning={field.showWarning}
                                                                            showFieldInfo={field.showFieldInfo}
                                                                            maxLength={field.maxLength}
                                                                            onInput={onInput || (isNew ? undefined : this.adminUpdate)}
                                                                            callback={(value) => {
                                                                                if (!field.callback) return null;
                                                                                if (field.callbackDebounce) {
                                                                                    this.debouncedCallback(field.name, field.callbackDebounce, () => field.callback(object, this.updateValue, holdingStore, value), value);
                                                                                } else {
                                                                                    field.callback(object, this.updateValue, holdingStore, value);
                                                                                }
                                                                            }}
                                                                            drawerCallback={() => field.drawerCallback ? field.drawerCallback(object, this.updateValue, holdingStore) : drawerCallback(object, this.updateValue, holdingStore)}
                                                                            loadCallback={() => field.loadCallback ? field.loadCallback(object, this.updateValue, holdingStore) : null}
                                                                            checkValue={(value) => field.checkValue ? field.checkValue(object, holdingStore, value) : null}
                                                                            before={() => field.before ? field.before(object, holdingStore) : null}
                                                                            onInputcallback={onInputcallback}
                                                                            extraDataset={{
                                                                                'data-isnew': isNew,
                                                                                'data-id': object.id,
                                                                                'data-md5': object.md5,
                                                                            }}
                                                                            drawerSize={field.drawerSize}
                                                                            drawerName={field.drawerName}
                                                                            drawerRight={field.drawerRight}
                                                                            width={field.displayWidth}
                                                                            height={field.displayHeight}
                                                                            lookupArray={field.lookupArray || section.lookupArray || lookupArray}
                                                                            foundListName={field.foundListName || section.foundListName}
                                                                            foundListKeysName={field.foundListKeysName || section.foundListKeysName}
                                                                            updateInProgress={updateInProgress[`${object.id}-${field.name}`]}
                                                                            onFocus={(e) => this.onFocus(e, field, object, holdingStore, objectName)}
                                                                            onBlur={this.onBlur}
                                                                            onKeyDown={onKeyDown}
                                                                            onPaste={field.onPaste ? (e) => field.onPaste(e, object, this.updateValue, isNew) : undefined}
                                                                            skipSurroundingDivs={skipSurroundingDivs}
                                                                            addToImages={field.addToImages}
                                                                            usefulValues={field.usefulValues}
                                                                            toggleFieldFunction={toggleFieldFunction}
                                                                            toggleFieldValues={toggleFieldValues}
                                                                            openFieldSettings={openFieldSettings}
                                                                            isRequired={field.isRequired}
                                                                            classNames='compact-input'
                                                                            addFileText={field.addFileText}
                                                                            displayLargeImages={field.displayLargeImages}
                                                                            displayLargeFiles={field.displayLargeFiles}
                                                                            showBackgroundImage={field.showBackgroundImage}
                                                                            addNameAfterFieldname={field.addNameAfterFieldname}
                                                                            showEmojis={field.showEmojis}
                                                                            showBackgroundSelector={field.showBackgroundSelector}
                                                                            backgroundColorOptions={field.backgroundColorOptions}
                                                                            isTable={isToggle}
                                                                        />
                                                                    </Localizer>
                                                                    {updateInProgress[`${object.id}-${field.name}`] && (
                                                                        <span class='compact-indicator'>
                                                                            <span class='compact-update-indicator' />
                                                                        </span>
                                                                    )}
                                                                    {field.isRequired && <span class='compact-required-float'>*</span>}
                                                                    {field.isRecommended && <span class='compact-recommended-float'>◆</span>}
                                                                    {!field.hideTextTemplate && isTextarea && (
                                                                        <div class='compact-text-template-actions'>
                                                                            {textTpls.length > 0 && (
                                                                                <button
                                                                                    class='compact-text-template-btn'
                                                                                    onClick={() => this.openTemplateModal(section.key, field.name)}
                                                                                    title={`Templates (${textTpls.length})`}
                                                                                >
                                                                                    <i class='fa-solid fa-file-lines me-1' />
                                                                                    Maler ({textTpls.length})
                                                                                </button>
                                                                            )}
                                                                            {isCustomerAdmin && object[field.name] && isInfocus[field.name] && (
                                                                                <button
                                                                                    class='compact-text-template-btn'
                                                                                    onClick={this.saveTextTemplate}
                                                                                    data-section={section.key}
                                                                                    data-field={field.name}
                                                                                >
                                                                                    <i class='fa-solid fa-floppy-disk me-1' />
                                                                                    {textTemplateSaved[field.name] ? 'Lagret!' : 'Lagre mal'}
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                    {field.displayInputInfo && field.displayInputInfo(object, holdingStore, objectName)}
                                                                </div>
                                                            </div>
                                                            {aside && <div class='compact-field-split-aside'>{aside}</div>}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Template Modal */}
                {showTemplateModal && (
                    <div class='modal show d-block' style='background: rgba(0,0,0,0.5);'>
                        <div class='modal-dialog modal-lg modal-dialog-scrollable'>
                            <div class='modal-content' style='overflow: hidden;'>
                                <div class='modal-header' style='border-bottom: 1px solid #e5e5ea;'>
                                    <h5 class='modal-title' style='font-weight: 600;'>
                                        <i class='fa-solid fa-file-lines me-2' />
                                        Choose Template
                                    </h5>
                                    <button type='button' class='btn-close' onClick={this.closeTemplateModal} />
                                </div>
                                <div class='modal-body'>
                                    <div class='row'>
                                        <div class='col-md-6'>
                                            <input
                                                type='text'
                                                class='form-control mb-3'
                                                placeholder={searchTemplatePlaceholder}
                                                value={templateSearchQuery}
                                                onInput={this.handleTemplateSearch}
                                            />
                                            <div class='list-group' style='max-height: 350px; overflow-y: auto;'>
                                                {textTemplates
                                                    .filter(t =>
                                                        t.section === templateModalSection &&
                                                        t.field === templateModalField &&
                                                        (templateSearchQuery === '' ||
                                                         t.body?.toLowerCase().includes(templateSearchQuery.toLowerCase()))
                                                    )
                                                    .sort(compareTextTemplates)
                                                    .map(t => (
                                                        <button
                                                            type='button'
                                                            class={`list-group-item list-group-item-action ${selectedTemplatePreview?.id === t.id ? 'active' : ''}`}
                                                            onClick={() => this.selectTemplateFromModal(t)}
                                                        >
                                                            <small style='opacity: 0.6;'>{t.title}</small>
                                                            <div style='font-size: 13px; line-height: 1.3;'>
                                                                {t.body?.substring(0, 80)}{t.body?.length > 80 ? '...' : ''}
                                                            </div>
                                                        </button>
                                                    ))}
                                            </div>
                                        </div>
                                        <div class='col-md-6'>
                                            <h6 style='font-weight: 600; margin-bottom: 12px;'>Preview</h6>
                                            {selectedTemplatePreview ? (
                                                <div class='card'>
                                                    <div class='card-body' style='font-size: 14px; line-height: 1.5;'>
                                                        <Markdown markdown={this.resolveTemplateBody(selectedTemplatePreview)} markedOpts={MARKDOWN_OPTIONS} />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div class='text-muted text-center py-5'>
                                                    <i class='fa-solid fa-file-lines fa-2x mb-2' style='opacity: 0.3;' />
                                                    <p>Select a template</p>
                                                </div>
                                            )}
                                            {!!this.props.templateFields && (
                                                <small class='d-block text-body-secondary mt-2'>
                                                    <Text id='texttemplate.fields-hint'>Codes filled in automatically:</Text>{' '}
                                                    {this.getTemplateFieldTokens().join(' ')}
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div class='modal-footer' style='border-top: 1px solid #e5e5ea;'>
                                    <button class='btn btn-secondary' onClick={this.closeTemplateModal}>
                                        Cancel
                                    </button>
                                    <button
                                        class='btn btn-primary'
                                        onClick={this.applySelectedTemplate}
                                        disabled={!selectedTemplatePreview}
                                    >
                                        Use Template
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default FormFieldsCompact;
